/**
 * AK-446 — какие действия по посеву показывать при каком статусе.
 *
 * Одно правило на все экраны (wireframe v4, `docs/superpowers/prototypes/
 * ak-446-sowing-actions/index.html`, утверждён founder 31.07 → PR #601):
 * **в каждый момент показываем действия только текущего статуса**. Не
 * показываем «Посеять» на убранном и «Убрать» на пустом — иначе кнопки
 * перестают что-либо значить, а агроном перестаёт им доверять.
 *
 * Матрица (экран «МАТРИЦА» wireframe'а):
 *
 * | Что видит агроном | Кнопки |
 * |---|---|
 * | Пусто (посева на сезон нет) | Создать посев |
 * | Запланировано | Посеять |
 * | Растёт (в т.ч. «посеяно X из Y га») | Убрать · Списать |
 * | Убрано / Списано | нет — сезон по посеву закрыт |
 * | Пар без культуры (чёрный, полупар) | нет — его не сеют и не убирают |
 * | Пар с культурой (сидеральный, занятый) | полный цикл (ADR-059 §D15) |
 *
 * Модуль намеренно **чистый** (без React и без сети): та же матрица нужна
 * строке зоны, блоку действий и — позже — мобильному экрану, а разъехавшиеся
 * копии условий это гарантированный «на карточке одно, в зоне другое».
 */
import { isOutcomeStage, sowingStage, type SowingStageSource } from './stage';

/** Действия из wireframe v4. Их ровно четыре, пятое не заводим без founder. */
export type SowingActionCode = 'create' | 'sow' | 'harvest' | 'writeoff';

/**
 * Минимум про занятие, нужный матрице. Совместим с `OccupancySowing`
 * карточки поля; тип не импортируем, чтобы модулем могли пользоваться карта и
 * мобильные экраны.
 */
export interface SowingActionSource extends SowingStageSource {
  field_state_id?: string | null;
  fallow_kind?: string | null;
}

/**
 * Пар БЕЗ культуры — чёрный / полупар. Прогресса не имеет вовсе (ADR-059
 * §D15): его не сеют и не убирают, поэтому действий нет. Пар С культурой
 * (сидеральный, занятый) идёт полным циклом — сидерат реально растёт в поле.
 */
export function isBareFallow(sowing?: SowingActionSource | null): boolean {
  if (!sowing || sowing.occupancy_type !== 'fallow') return false;
  return !(sowing.crop_id ?? sowing.local_catalog_item_id ?? sowing.crop_name);
}

/**
 * Действия для занятия. `null`/`undefined` = посева на сезон нет — это не
 * статус, а его отсутствие (решение founder 31.07), и единственное осмысленное
 * действие здесь «Создать посев».
 */
export function sowingActions(
  sowing?: SowingActionSource | null,
): SowingActionCode[] {
  if (!sowing) return ['create'];
  if (isBareFallow(sowing)) return [];

  const stage = sowingStage(sowing);
  // Терминал — сезон по посеву закрыт. Ошиблись — правится через бейдж, где
  // записывается причина; кнопки это правило повторяют, а не ломают.
  if (isOutcomeStage(stage)) return [];

  switch (stage) {
    case 'planned':
      return ['sow'];
    // «Растёт» покрывает бывшие «Посеян / Посеян частично / Вегетация /
    // Убирают»: культура в поле, дальше её либо убирают, либо списывают.
    // Различия ушли в цифру рядом (ADR-059 §D11), не в набор кнопок.
    case 'sown':
    case 'partial_sown':
    case 'growing':
    case 'partial_harvest':
    case 'reseeded':
      return ['harvest', 'writeoff'];
    // 'unplanned' без культуры и 'fallow' — сюда не доходят (отсечены выше),
    // всё прочее — незнакомый код: молчим, а не выдумываем действие.
    default:
      return [];
  }
}

/** Подпись действия — одна на карточку, строку зоны и мобильный экран. */
export const SOWING_ACTION_LABELS: Record<SowingActionCode, string> = {
  create: 'Создать посев',
  sow: 'Посеять',
  harvest: 'Убрать',
  writeoff: 'Списать',
};

/**
 * Главное действие стадии рисуется основной кнопкой, остальные — обычной.
 * «Списать» — редкое и неприятное событие, оно не должно стоять рядом с
 * уборкой одинаковым весом (аннотация 2 экрана Г).
 */
export function isPrimaryAction(code: SowingActionCode): boolean {
  return code !== 'writeoff';
}
